/**
 * Extract the file extension from a download URL's pathname.
 * Signed S3 URLs end with query params, so parse the pathname — the stored
 * S3 object key preserves the real extension written by the server, whatever
 * the export type (no allowlist to keep in sync with new export formats).
 *
 * Returns null when the pathname has no extension so callers fall back to a
 * content-type-derived filename instead of mislabeling the download
 * (FIR-7987: zip archives were saved as ".csv").
 */
export function getExtensionFromUrl(downloadUrl: string): string | null {
  // Strip query string via URL parsing; fall back to manual split for relative URLs.
  let pathname = downloadUrl
  try {
    pathname = new URL(downloadUrl).pathname
  } catch {
    pathname = downloadUrl.split('?')[0]
  }
  return pathname.toLowerCase().match(/\.[a-z0-9]+$/)?.[0] ?? null
}

/**
 * Extract error message from various API response formats.
 * Handles blob, JSON, ArrayBuffer, and axios error responses.
 */
export async function extractApiError(error: unknown): Promise<string> {
  const defaultMessage = 'Download failed'

  // Check if it's an axios error with response data
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { data?: unknown } }
    const responseData = axiosError.response?.data

    // Handle blob response (need to parse it)
    if (responseData instanceof Blob) {
      try {
        const text = await responseData.text()
        const json = JSON.parse(text)
        return json.detail || json.error || defaultMessage
      } catch (e) {
        console.error('Failed to parse error blob response', e)
        return defaultMessage
      }
    }

    // Handle JSON response
    if (
      !(responseData instanceof ArrayBuffer) &&
      responseData &&
      typeof responseData === 'object'
    ) {
      const data = responseData as { detail?: string; error?: string }
      return data.detail || data.error || defaultMessage
    }

    // Handle ArrayBuffer response
    if (responseData instanceof ArrayBuffer) {
      const data = JSON.parse(new TextDecoder().decode(responseData)) as {
        detail?: string
        error?: string
      }
      return data.detail || data.error || defaultMessage
    }
  }

  // Handle Error instances
  if (error instanceof Error) {
    return error.message
  }

  return defaultMessage
}
